/**
 * CopilotProvider — Kontext für die global angedockte KI-Chat-Spalte (MAESTRO 2.0:
 * „Copilot rechts, bestehende Software bleibt"). Hält:
 *   (a) EINE geteilte useAgentSession-Instanz, damit die Spalte über Seitenwechsel denselben
 *       Verlauf behält (der Provider umschließt AppShell UND den Router-Outlet der Seiten).
 *   (b) isOpen / open / close / toggle für das Ein- und Ausklappen der Spalte
 *       (persistiert in localStorage; Default: offen ab lg, zu auf mobil).
 *   (c) ein leichtes Event-Bus-Gerüst für SPÄTER (Schritt A2): requestNavigate/navigateTarget
 *       sowie subscribeContentChanged/emitContentChanged. Die tatsächliche Verdrahtung von
 *       navigate/refetch in den Seiten folgt separat — hier steht nur die Provider-API bereit.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useAgentSession, type AgentSession, type ReportContextPayload } from "../hooks/useAgentSession";
import { useFloatingWindow, type FloatingWindowApi } from "../hooks/useFloatingWindow";
import { apiFetch, toApiError } from "../lib/api";

const STORAGE_KEY = "maestro.copilot.open";
// 23e: welche Unterhaltung zuletzt aktiv war (persistiert wie isOpen). Fehlt der Eintrag oder
// ist er leer, ist die Legacy-/Default-Sitzung aktiv (identisches Verhalten wie vor 23e).
const CONVERSATION_STORAGE_KEY = "maestro.copilot.activeConversation";
const LG_QUERY = "(min-width: 1024px)"; // Tailwind lg-Breakpoint

/** Metadaten einer Unterhaltung (Verlauf-Dropdown) - der Nachrichteninhalt bleibt im Agent-DO. */
export interface AgentConversationSummary {
  id: string;
  title: string | null;
  createdAt: string;
  lastMessageAt: string;
}

/**
 * Nutzlast des contentChanged-Signals (Response-Vertrag A2): welche Ressource/Zeile die
 * betroffene Seite neu laden soll. Der Agent liefert genau dieses Format als `changed`.
 */
export interface ContentChangedPayload {
  /** Logische Ressource, deren Daten sich geändert haben (z. B. "catalog", "offer"). */
  resource: string;
  /** Optionale ID der betroffenen Entität/Zeile. */
  id?: string;
}
export type ContentChangedListener = (payload: ContentChangedPayload) => void;

export interface CopilotContextValue {
  /** Geteilte Chat-Session (Verlauf, Kacheln, send/approve/…). */
  session: AgentSession;
  /**
   * Fenster-Geometrie/-Modus des Assistenten (schweben/andocken/verankern/einklappen) — bewusst
   * HIER statt lokal in CopilotPanel instanziiert (App-weite Erweiterung 28.08.2026): AppShell
   * braucht `mode`, um im "pinned"-Modus für die Hauptfläche Platz zu reservieren (echte,
   * verdrängende rechte Spalte statt Überlagerung), und CopilotProvider ist der einzige Ort, der
   * bereits beide — AppShell UND CopilotPanel — umschließt (App.tsx: ProtectedLayout).
   */
  floatWin: FloatingWindowApi;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  /** Von einer Kachel/Aktion angefordertes Navigationsziel (A2 verdrahtet das Ausführen). */
  navigateTarget: string | null;
  requestNavigate: (path: string | null) => void;
  /** Abonniert „Inhalt hat sich geändert"-Signale; liefert die Abmelde-Funktion. */
  subscribeContentChanged: (listener: ContentChangedListener) => () => void;
  emitContentChanged: (payload: ContentChangedPayload) => void;
  /** Slice 3 (§6.4): Seite meldet einen Klick-Fehler strukturiert - öffnet die Spalte (dezent) und
   *  lässt den Copilot dieselbe geführte Antwort formulieren wie beim Chat-Pfad. */
  reportContext: (payload: ReportContextPayload) => void;
  // 23e: Verlauf mehrerer Unterhaltungen. null = Legacy-/Default-Sitzung (keine agent_conversations-
  // Zeile - identisches Verhalten wie vor 23e).
  activeConversationId: string | null;
  conversations: AgentConversationSummary[];
  conversationsLoading: boolean;
  /** Legt eine neue, leere Unterhaltung an und wechselt sofort dorthin. */
  startNewConversation: () => Promise<void>;
  /** Wechselt die aktive Unterhaltung (null = zurueck zur Legacy-/Default-Sitzung). */
  switchConversation: (id: string | null) => void;
  reloadConversations: () => Promise<void>;
}

const CopilotContext = createContext<CopilotContextValue | null>(null);

/** Startzustand: gespeicherte Präferenz gewinnt, sonst offen ab lg-Breakpoint. */
function initialOpen(): boolean {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "1") return true;
  if (stored === "0") return false;
  return window.matchMedia(LG_QUERY).matches;
}

export function CopilotProvider({ children }: { children: ReactNode }) {
  const floatWin = useFloatingWindow();
  const [isOpen, setIsOpen] = useState<boolean>(initialOpen);
  const [navigateTarget, setNavigateTarget] = useState<string | null>(null);
  const listenersRef = useRef<Set<ContentChangedListener>>(new Set());
  // 23e: aktive Unterhaltung - gespeicherte Präferenz gewinnt (wie isOpen), sonst Legacy-Sitzung.
  const [activeConversationId, setActiveConversationId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(CONVERSATION_STORAGE_KEY) || null;
  });
  const [conversations, setConversations] = useState<AgentConversationSummary[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);

  const requestNavigate = useCallback((path: string | null) => setNavigateTarget(path), []);
  const emitContentChanged = useCallback((payload: ContentChangedPayload) => {
    listenersRef.current.forEach((listener) => listener(payload));
  }, []);

  // A2a: Die geteilte Session zieht navigate/changed aus den Agent-Antworten und reicht sie an den
  // Bus — navigate -> requestNavigate (AppShell führt es im Router aus, A2b), changed ->
  // emitContentChanged (betroffene Seiten refetchen via useContentRefetch, A2c).
  // 23e: conversationId durchgereicht - useAgentSession leert Verlauf/Kacheln automatisch und
  // laedt den Verlauf der neuen Unterhaltung, sobald sich activeConversationId aendert.
  const session = useAgentSession({ onNavigate: requestNavigate, onContentChanged: emitContentChanged, conversationId: activeConversationId });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, isOpen ? "1" : "0");
  }, [isOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (activeConversationId) window.localStorage.setItem(CONVERSATION_STORAGE_KEY, activeConversationId);
    else window.localStorage.removeItem(CONVERSATION_STORAGE_KEY);
  }, [activeConversationId]);

  // 23e: Liste der Unterhaltungen fuer das Verlauf-Dropdown - nur Metadaten (Titel/Zeitstempel),
  // best-effort (ein Fehler hier darf den Chat selbst nie beeintraechtigen).
  const loadConversations = useCallback(async () => {
    try {
      const res = await apiFetch("/api/agent/conversations");
      if (!res.ok) throw await toApiError(res);
      const { data } = (await res.json()) as { data: AgentConversationSummary[] };
      setConversations(data ?? []);
    } catch {
      /* best-effort - die Legacy-Sitzung funktioniert auch ohne diese Liste. */
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  // Nach jedem abgeschlossenen Agent-Turn die Liste nachziehen (lastMessageAt-Sortierung/Titel
  // koennen sich geaendert haben) - reine Anzeige-Aktualisierung, kein Einfluss auf den Chat.
  const prevBusyRef = useRef(session.busy);
  useEffect(() => {
    if (prevBusyRef.current && !session.busy) void loadConversations();
    prevBusyRef.current = session.busy;
  }, [session.busy, loadConversations]);

  const switchConversation = useCallback((id: string | null) => setActiveConversationId(id), []);

  // 23e: id kommt vom Client, damit sie schon vor dem ersten INSERT als Durable-Object-Session-
  // Suffix feststeht (siehe apps/api/src/routes/agent.ts::resolveAgent). Schlaegt das Anlegen der
  // Metadaten-Zeile fehl, wird trotzdem gewechselt (der Thread funktioniert, erscheint dann nur
  // nicht im Dropdown) - "Neuer Chat" darf nie blockieren.
  const startNewConversation = useCallback(async () => {
    const id = crypto.randomUUID();
    try {
      const res = await apiFetch("/api/agent/conversations", { method: "POST", body: JSON.stringify({ id, title: null }) });
      if (!res.ok) throw await toApiError(res);
    } catch {
      /* siehe Kommentar oben - der Wechsel passiert trotzdem. */
    }
    setActiveConversationId(id);
    void loadConversations();
  }, [loadConversations]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const subscribeContentChanged = useCallback((listener: ContentChangedListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  // Slice 3: oeffnet die Spalte dezent und stoesst den Rueckkanal-Turn an (fire-and-forget aus
  // Sicht der aufrufenden Seite - session.reportContext() wirft nie, siehe useAgentSession).
  const reportContext = useCallback(
    (payload: ReportContextPayload) => {
      open();
      void session.reportContext(payload);
    },
    [open, session],
  );

  const value: CopilotContextValue = {
    session,
    floatWin,
    isOpen,
    open,
    close,
    toggle,
    navigateTarget,
    requestNavigate,
    subscribeContentChanged,
    emitContentChanged,
    reportContext,
    activeConversationId,
    conversations,
    conversationsLoading,
    startNewConversation,
    switchConversation,
    reloadConversations: loadConversations,
  };

  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
}

export function useCopilot(): CopilotContextValue {
  const ctx = useContext(CopilotContext);
  if (!ctx) throw new Error("useCopilot muss innerhalb von <CopilotProvider> verwendet werden.");
  return ctx;
}

/**
 * useContentRefetch (A2c) — abonniert das „Inhalt geändert"-Signal des Copilot und ruft `reload`,
 * sobald die gemeldete Ressource passt. Kapselt An-/Abmeldung sauber. KEIN globaler Voll-Reload,
 * sondern gezielt der useApi-Refetch der betroffenen Seite (die betroffene Liste/Zeile).
 * `resource` darf ein einzelner Name oder mehrere sein (z. B. ["catalog", "offer"]).
 *
 * Optionales `id`: Seiten, die genau EINE Entität zeigen (z. B. Kunden-Detail), übergeben ihre
 * aktuell offene id. Trägt das Signal eine konkrete `changed.id`, wird dann nur bei Übereinstimmung
 * neu geladen — ein Agent-Apply an einem anderen Datensatz löst hier keinen Refetch aus. Ohne
 * `id`-Filter oder ohne `changed.id` genügt weiterhin der reine Ressourcen-Treffer (Bestandsverhalten
 * der bisherigen Aufrufe bleibt unverändert).
 */
export function useContentRefetch(resource: string | string[], reload: () => void, id?: string): void {
  const { subscribeContentChanged } = useCopilot();
  // reload in einem Ref halten -> das Abo muss nicht neu aufgesetzt werden, wenn sich die
  // (bei jedem Render neu erzeugte) reload-Closure ändert.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const key = Array.isArray(resource) ? resource.join(",") : resource;
  useEffect(() => {
    const resources = key.split(",");
    return subscribeContentChanged((payload) => {
      if (!resources.includes(payload.resource)) return;
      // id-Filter (falls gesetzt) nur anwenden, wenn das Signal selbst eine id trägt — sonst
      // (ressourcenweite Änderung ohne konkrete Zeile) weiterhin neu laden.
      if (id != null && payload.id != null && payload.id !== id) return;
      reloadRef.current();
    });
  }, [subscribeContentChanged, key, id]);
}
